import { useEffect, useState } from 'react';
import { Skeleton } from '../Skeleton';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { IngredientCategory, shelfLifeAdjustmentDtoSchema } from '@fewerbags/schema';
import type { z } from 'zod';
import { Alert } from '../Alert';
import { Button } from '../Button';
import { Field } from '../Field';
import { SelectField } from '../SelectField';
import { api } from '../../lib/api';
import { messageForError } from '../../lib/errors';
import { storeQueryKeys } from '../../pages/stores/queryKeys';
import { CATEGORY_LABEL, CATEGORY_OPTIONS } from './labels';

type ShelfLifeAdjustmentDto = z.infer<typeof shelfLifeAdjustmentDtoSchema>;

interface RuleDraft {
  adjustDays: string;
  adjustPct: string;
  note: string;
}

const EMPTY_DRAFT: RuleDraft = { adjustDays: '', adjustPct: '', note: '' };

function toDraft(a: ShelfLifeAdjustmentDto | undefined): RuleDraft {
  if (!a) return EMPTY_DRAFT;
  return {
    adjustDays: a.adjustDays === null ? '' : String(a.adjustDays),
    adjustPct: a.adjustPct === null ? '' : String(a.adjustPct),
    note: a.note ?? '',
  };
}

/** A rule with no adjustment at all is functionally cleared (see the "remove"
 *  note below) — don't show it as an active row. */
function isActive(a: ShelfLifeAdjustmentDto): boolean {
  return a.adjustDays !== null || a.adjustPct !== null;
}

/**
 * Whole-store and per-category shelf-life adjustments (D6). The API only
 * exposes an upsert — there is no delete endpoint for either the whole-store
 * rule or a category rule — so "remove" clears both values instead.
 */
export function ShelfLifeSection({ storeId }: { storeId: string }) {
  const qc = useQueryClient();
  const shelfLife = useQuery({
    queryKey: storeQueryKeys.shelfLife(storeId),
    queryFn: () => api.stores.shelfLife.list(storeId),
  });

  const wholeStore = shelfLife.data?.adjustments.find((a) => a.ingredientCategory === null);
  const categoryRules = (shelfLife.data?.adjustments ?? []).filter(
    (a): a is ShelfLifeAdjustmentDto & { ingredientCategory: IngredientCategory } =>
      a.ingredientCategory !== null && isActive(a),
  );
  const usedCategories = new Set(categoryRules.map((a) => a.ingredientCategory));

  const [wholeDraft, setWholeDraft] = useState<RuleDraft>(EMPTY_DRAFT);
  useEffect(() => setWholeDraft(toDraft(wholeStore)), [wholeStore]);

  const [editingCategory, setEditingCategory] = useState<IngredientCategory | null>(null);
  const [categoryDraft, setCategoryDraft] = useState<RuleDraft>(EMPTY_DRAFT);
  const [addingCategory, setAddingCategory] = useState<IngredientCategory | ''>('');

  const invalidate = () => qc.invalidateQueries({ queryKey: storeQueryKeys.shelfLife(storeId) });

  const upsert = useMutation({
    mutationFn: (input: {
      ingredientCategory: IngredientCategory | null;
      adjustDays: number | null;
      adjustPct: number | null;
      note: string | null;
    }) => api.stores.shelfLife.upsert(storeId, input),
    onSuccess: () => {
      invalidate();
      setEditingCategory(null);
      setAddingCategory('');
    },
  });

  function parseDraft(draft: RuleDraft): { adjustDays: number | null; adjustPct: number | null } {
    return {
      adjustDays: draft.adjustDays.trim() === '' ? null : Math.round(Number(draft.adjustDays)),
      adjustPct: draft.adjustPct.trim() === '' ? null : Number(draft.adjustPct),
    };
  }

  function saveWholeStore() {
    const parsed = parseDraft(wholeDraft);
    upsert.mutate({
      ingredientCategory: null,
      ...parsed,
      note: wholeDraft.note.trim() ? wholeDraft.note.trim() : null,
    });
  }

  function clearWholeStore() {
    upsert.mutate({ ingredientCategory: null, adjustDays: null, adjustPct: null, note: null });
  }

  function saveCategory(category: IngredientCategory, draft: RuleDraft) {
    const parsed = parseDraft(draft);
    upsert.mutate({
      ingredientCategory: category,
      ...parsed,
      note: draft.note.trim() ? draft.note.trim() : null,
    });
  }

  function clearCategory(category: IngredientCategory) {
    upsert.mutate({ ingredientCategory: category, adjustDays: null, adjustPct: null, note: null });
  }

  const availableCategories = CATEGORY_OPTIONS.filter((c) => !usedCategories.has(c.value));

  return (
    <div className="flex flex-col gap-5">
      {shelfLife.isPending ? <Skeleton lines={3} /> : null}
      {shelfLife.isError ? <Alert tone="error">{messageForError(shelfLife.error)}</Alert> : null}

      <div className="flex flex-col gap-3">
        <h3 className="text-sm font-semibold text-brand">Whole store</h3>
        <p className="text-xs text-muted">
          Applies to every ingredient at this store unless a category rule below overrides it.
        </p>
        <div className="grid grid-cols-2 gap-3">
          <Field
            id="shelflife-store-days"
            label="Adjust days"
            type="number"
            inputMode="numeric"
            step="1"
            min={-60}
            max={60}
            placeholder="e.g. −2"
            value={wholeDraft.adjustDays}
            onChange={(e) => setWholeDraft((d) => ({ ...d, adjustDays: e.target.value }))}
          />
          <Field
            id="shelflife-store-pct"
            label="Adjust %"
            type="number"
            inputMode="decimal"
            step="any"
            min={-90}
            max={200}
            placeholder="e.g. 10"
            value={wholeDraft.adjustPct}
            onChange={(e) => setWholeDraft((d) => ({ ...d, adjustPct: e.target.value }))}
          />
        </div>
        <Field
          id="shelflife-store-note"
          label="Note (optional)"
          value={wholeDraft.note}
          onChange={(e) => setWholeDraft((d) => ({ ...d, note: e.target.value }))}
        />
        {upsert.isError ? <Alert tone="error">{messageForError(upsert.error)}</Alert> : null}
        <div className="flex gap-2">
          <Button
            variant="secondary"
            className="flex-1"
            onClick={saveWholeStore}
            disabled={upsert.isPending}
          >
            Save
          </Button>
          {wholeStore && isActive(wholeStore) ? (
            <Button
              variant="danger"
              className="flex-1"
              onClick={clearWholeStore}
              disabled={upsert.isPending}
            >
              Clear
            </Button>
          ) : null}
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <h3 className="text-sm font-semibold text-brand">Category adjustments</h3>
        {categoryRules.length === 0 ? (
          <p className="text-sm text-muted">No category-specific rules yet.</p>
        ) : (
          <ul className="flex flex-col divide-y divide-line rounded-lg border border-line">
            {categoryRules.map((rule) => {
              const category = rule.ingredientCategory;
              const isEditing = editingCategory === category;
              return (
                <li key={category} className="flex flex-col gap-2 px-3 py-2">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium text-ink">{CATEGORY_LABEL[category]}</span>
                    <span className="text-sm text-muted">
                      {[
                        rule.adjustDays !== null
                          ? `${rule.adjustDays > 0 ? '+' : ''}${rule.adjustDays} days`
                          : null,
                        rule.adjustPct !== null
                          ? `${rule.adjustPct > 0 ? '+' : ''}${rule.adjustPct}%`
                          : null,
                      ]
                        .filter(Boolean)
                        .join(', ')}
                    </span>
                  </div>
                  {rule.note ? <p className="text-xs text-muted">{rule.note}</p> : null}
                  {isEditing ? (
                    <div className="flex flex-col gap-2 rounded-lg border border-line bg-cream/40 p-2">
                      <div className="grid grid-cols-2 gap-2">
                        <Field
                          id={`shelflife-cat-days-${category}`}
                          label="Adjust days"
                          type="number"
                          inputMode="numeric"
                          step="1"
                          min={-60}
                          max={60}
                          value={categoryDraft.adjustDays}
                          onChange={(e) =>
                            setCategoryDraft((d) => ({ ...d, adjustDays: e.target.value }))
                          }
                        />
                        <Field
                          id={`shelflife-cat-pct-${category}`}
                          label="Adjust %"
                          type="number"
                          inputMode="decimal"
                          step="any"
                          min={-90}
                          max={200}
                          value={categoryDraft.adjustPct}
                          onChange={(e) =>
                            setCategoryDraft((d) => ({ ...d, adjustPct: e.target.value }))
                          }
                        />
                      </div>
                      <Field
                        id={`shelflife-cat-note-${category}`}
                        label="Note (optional)"
                        value={categoryDraft.note}
                        onChange={(e) => setCategoryDraft((d) => ({ ...d, note: e.target.value }))}
                      />
                      <div className="flex gap-2">
                        <Button
                          className="flex-1"
                          onClick={() => saveCategory(category, categoryDraft)}
                          disabled={upsert.isPending}
                        >
                          Save
                        </Button>
                        <Button
                          variant="secondary"
                          className="flex-1"
                          onClick={() => setEditingCategory(null)}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setEditingCategory(category);
                          setCategoryDraft(toDraft(rule));
                        }}
                        className="min-h-9 rounded-lg border border-line px-3 text-sm text-ink"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => clearCategory(category)}
                        disabled={upsert.isPending}
                        className="min-h-9 rounded-lg border border-line px-3 text-sm text-fail"
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        {availableCategories.length > 0 ? (
          addingCategory ? (
            <div className="flex flex-col gap-2 rounded-lg border border-line bg-cream/40 p-2">
              <SelectField
                id="shelflife-add-category"
                label="Category"
                options={availableCategories}
                value={addingCategory}
                onChange={(e) => setAddingCategory(e.target.value as IngredientCategory)}
              />
              <div className="grid grid-cols-2 gap-2">
                <Field
                  id="shelflife-add-days"
                  label="Adjust days"
                  type="number"
                  inputMode="numeric"
                  step="1"
                  min={-60}
                  max={60}
                  value={categoryDraft.adjustDays}
                  onChange={(e) => setCategoryDraft((d) => ({ ...d, adjustDays: e.target.value }))}
                />
                <Field
                  id="shelflife-add-pct"
                  label="Adjust %"
                  type="number"
                  inputMode="decimal"
                  step="any"
                  min={-90}
                  max={200}
                  value={categoryDraft.adjustPct}
                  onChange={(e) => setCategoryDraft((d) => ({ ...d, adjustPct: e.target.value }))}
                />
              </div>
              <Field
                id="shelflife-add-note"
                label="Note (optional)"
                value={categoryDraft.note}
                onChange={(e) => setCategoryDraft((d) => ({ ...d, note: e.target.value }))}
              />
              <div className="flex gap-2">
                <Button
                  className="flex-1"
                  onClick={() => {
                    if (addingCategory) saveCategory(addingCategory, categoryDraft);
                  }}
                  disabled={upsert.isPending}
                >
                  Add rule
                </Button>
                <Button
                  variant="secondary"
                  className="flex-1"
                  onClick={() => {
                    setAddingCategory('');
                    setCategoryDraft(EMPTY_DRAFT);
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => {
                setAddingCategory(availableCategories[0]!.value);
                setCategoryDraft(EMPTY_DRAFT);
              }}
              className="min-h-11 rounded-lg border border-dashed border-line px-4 text-sm font-medium text-brand hover:bg-brand-soft"
            >
              + Add a category rule
            </button>
          )
        ) : null}
      </div>
    </div>
  );
}
